import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Heart, MessageCircle, Eye, Sparkles, Ticket, ClipboardList, Calendar, TrendingUp, ArrowLeft } from "lucide-react";

export default function MerchantStatsPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/merchant/stats")
      .then((r) => setStats(r.data))
      .catch(() => setStats(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <p className="text-plum-700/50 italic py-12 text-center">Chargement…</p>;
  if (!stats) return <p className="text-plum-700/50 italic py-12 text-center">Aucune donnée pour le moment.</p>;

  const conv = stats.vouchers.conversion_rate;
  const convColor = conv >= 50 ? "text-mint-600" : conv >= 25 ? "text-sun-700" : "text-pink-600";

  return (
    <div className="space-y-8" data-testid="merchant-stats-page">
      <header>
        <Link to="/commercant" className="inline-flex items-center gap-1.5 text-[12px] text-plum-700/65 hover:text-plum-700 mb-3">
          <ArrowLeft size={12} /> Retour au tableau de bord
        </Link>
        <div className="phenix-eyebrow mb-3">Analytics</div>
        <h1 className="heading-display text-2xl lg:text-3xl text-plum-900">
          Vos <em className="text-pink-500">performances.</em>
        </h1>
        <p className="text-[13px] text-plum-700/70 mt-2">
          Suivez l&apos;engagement de vos posts, sondages, événements et bons en temps réel.
        </p>
      </header>

      {/* Top KPIs */}
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="merchant-stats-kpis">
        <KPI icon={Heart} label="Réactions" value={stats.posts.reactions} color="bg-pink-100 text-pink-600" />
        <KPI icon={MessageCircle} label="Commentaires" value={stats.posts.comments} color="bg-plum-100 text-plum-700" />
        <KPI icon={Eye} label="Vues posts" value={stats.posts.views} color="bg-sun-100 text-sun-700" />
        <KPI icon={Ticket} label="Bons distribués" value={stats.vouchers.issued} color="bg-mint-100 text-mint-600" />
      </section>

      {/* Mur */}
      <Section title="📣 Mur des commerçants" subtitle={`${stats.posts.total} post${stats.posts.total > 1 ? "s" : ""} publié${stats.posts.total > 1 ? "s" : ""}`}>
        <div className="grid sm:grid-cols-3 gap-3">
          <Row label="Engagement moyen / post" value={stats.posts.engagement_per_post} suffix="actions" />
          <Row label="Total réactions" value={stats.posts.reactions} />
          <Row label="Total vues" value={stats.posts.views} />
        </div>
        {stats.posts.top_post && (
          <div className="mt-4 phenix-card p-4 bg-cream-50/50">
            <div className="phenix-eyebrow mb-2 flex items-center gap-1.5">
              <TrendingUp size={11} className="text-pink-500" /> Post le plus engageant
            </div>
            <p className="text-[13px] text-plum-900 line-clamp-2 mb-2">{stats.posts.top_post.text}</p>
            <div className="flex items-center gap-4 text-[11px] text-plum-700/65">
              <span className="inline-flex items-center gap-1"><Heart size={11} /> {stats.posts.top_post.reactions}</span>
              <span className="inline-flex items-center gap-1"><MessageCircle size={11} /> {stats.posts.top_post.comments}</span>
              <span className="inline-flex items-center gap-1"><Eye size={11} /> {stats.posts.top_post.views}</span>
            </div>
          </div>
        )}
      </Section>

      {/* Sondages */}
      <Section title="🗳 Sondages" subtitle={`${stats.surveys.total} sondage${stats.surveys.total > 1 ? "s" : ""} créé${stats.surveys.total > 1 ? "s" : ""}`}>
        <div className="grid sm:grid-cols-3 gap-3">
          <Row label="Sondages actifs" value={stats.surveys.active} />
          <Row label="Réponses totales" value={stats.surveys.total_responses} />
          <Row label="Réponses moyennes / sondage" value={stats.surveys.avg_responses} />
        </div>
      </Section>

      {/* Bons */}
      <Section title="🎟 Bons de réduction" subtitle="Conversion : du sondage à l'achat en boutique">
        <div className="grid sm:grid-cols-4 gap-3">
          <Row label="Émis" value={stats.vouchers.issued} />
          <Row label="Utilisés" value={stats.vouchers.redeemed} highlight />
          <Row label="Disponibles" value={stats.vouchers.available} />
          <Row label="Réduction moyenne" value={`${stats.vouchers.avg_percent}%`} />
        </div>
        <div className="mt-4 phenix-card p-5 bg-gradient-to-br from-mint-50 to-cream-50">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="phenix-eyebrow mb-1">Taux de conversion</div>
              <div className={`font-display text-4xl font-extrabold leading-none ${convColor}`} data-testid="merchant-stats-conversion">
                {stats.vouchers.conversion_rate}%
              </div>
              <p className="text-[11px] text-plum-700/65 mt-1.5 max-w-xs">
                {stats.vouchers.redeemed} bons utilisés sur {stats.vouchers.issued} émis
              </p>
            </div>
            <div className="text-[10px] text-plum-700/55 max-w-[200px] leading-tight">
              <strong>Bon score :</strong> ≥ 50% · Acceptable : 25–50% · À booster : &lt; 25%
            </div>
          </div>
        </div>
      </Section>

      {/* Events */}
      <Section title="📅 Événements" subtitle="Annonces sur le Mur + Agenda public">
        <div className="grid sm:grid-cols-2 gap-3">
          <Row label="Événements publiés" value={stats.events.total} />
          <Row label="À venir" value={stats.events.upcoming} highlight />
        </div>
      </Section>
    </div>
  );
}

function KPI({ icon: Icon, label, value, color }) {
  return (
    <div className="phenix-card p-4 flex items-start gap-3">
      <div className={`w-10 h-10 rounded-xl ${color} grid place-items-center shrink-0`}>
        <Icon size={17} strokeWidth={2.2} />
      </div>
      <div className="min-w-0">
        <div className="font-display font-extrabold text-2xl text-plum-900 leading-none tabular-nums">{value}</div>
        <div className="text-[10px] text-plum-700/60 mt-1 uppercase tracking-wider">{label}</div>
      </div>
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <section className="phenix-card p-5 bg-white">
      <header className="mb-4">
        <h2 className="font-display font-bold text-lg text-plum-900">{title}</h2>
        {subtitle && <p className="text-[12px] text-plum-700/65 mt-0.5">{subtitle}</p>}
      </header>
      {children}
    </section>
  );
}

function Row({ label, value, suffix, highlight }) {
  return (
    <div className={`rounded-xl border px-3.5 py-2.5 ${highlight ? "bg-mint-50 border-mint-200" : "bg-cream-50/40 border-cream-200"}`}>
      <div className="text-[10px] text-plum-700/60 uppercase tracking-wider">{label}</div>
      <div className="font-display font-extrabold text-xl text-plum-900 leading-tight mt-0.5 tabular-nums">
        {value}{suffix && <span className="text-[11px] font-normal text-plum-700/55 ml-1">{suffix}</span>}
      </div>
    </div>
  );
}
